import { motion } from "framer-motion";
import { Shovel, Sparkles, ShieldCheck, Wrench, RefreshCcw, Settings2, ArrowRight } from "lucide-react";

const SERVICES = [
  {
    icon: Shovel,
    title: "Industrial Manure Removal",
    desc: "Efficient, thorough removal of barn waste to improve sanitation and flock health.",
    span: "md:col-span-4 md:row-span-2",
    dark: true,
  },
  {
    icon: Sparkles,
    title: "Professional Barn Cleaning",
    desc: "Deep cleaning and disinfecting services tailored to modern poultry facilities.",
    span: "md:col-span-4",
    dark: false,
  },
  {
    icon: ShieldCheck,
    title: "Agricultural Sanitation",
    desc: "Biosecurity protocols and sanitation solutions to safeguard your operations.",
    span: "md:col-span-4",
    dark: false,
  },
  {
    icon: Wrench,
    title: "Barn Maintenance",
    desc: "Facility cleanliness and upkeep for poultry housing and production areas.",
    span: "md:col-span-4",
    dark: false,
  },
  {
    icon: RefreshCcw,
    title: "Flock Turnaround Prep",
    desc: "Preparation of poultry barns between production cycles, ready for the next flock.",
    span: "md:col-span-4",
    dark: false,
  },
  {
    icon: Settings2,
    title: "Custom Cleaning Programs",
    desc: "Customized cleaning solutions built around your farm's individual requirements.",
    span: "md:col-span-8",
    dark: false,
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.07, ease: "easeOut" } }),
};

export const Services = () => {
  return (
    <section id="services" data-testid="services-section" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="max-w-2xl mb-14">
          <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-[#1B4332] mb-4">Our Services</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-[#081C15]" data-testid="services-heading">
            Complete barn cleaning &amp; sanitation, end to end
          </h2>
          <p className="mt-4 text-base md:text-lg text-gray-700 leading-relaxed">
            From full manure removal to biosecurity-grade disinfecting — every service is built
            around your barn, your flock cycle, and your standards.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8" data-testid="services-grid">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.title}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                data-testid={`service-card-${i}`}
                className={`${s.span} rounded-xl border p-7 md:p-8 flex flex-col justify-between min-h-[190px] hover:-translate-y-1 transition-transform duration-300 ${
                  s.dark
                    ? "bg-[#1B4332] border-[#1B4332] text-white shadow-[0_12px_40px_rgba(27,67,50,0.25)]"
                    : "bg-[#F9FAF8] border-[#D8E2DC] hover:shadow-[0_10px_30px_rgba(27,67,50,0.08)]"
                }`}
              >
                <span
                  className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    s.dark ? "bg-[#D4AF37]" : "bg-[#1B4332]"
                  }`}
                >
                  <Icon size={22} className={s.dark ? "text-[#081C15]" : "text-[#D4AF37]"} />
                </span>
                {s.dark && (
                  <img
                    src="/imr-barn.jpg"
                    alt="IMR service barn in British Columbia"
                    data-testid="service-card-image"
                    className="my-6 rounded-lg w-full h-40 md:h-48 object-cover"
                  />
                )}
                <div className="mt-6">
                  <h3 className={`font-display font-bold text-xl ${s.dark ? "text-white" : "text-[#081C15]"}`}>
                    {s.title}
                  </h3>
                  <p className={`mt-2 text-sm leading-relaxed ${s.dark ? "text-[#D8E2DC]" : "text-gray-600"}`}>
                    {s.desc}
                  </p>
                </div>
              </motion.article>
            );
          })}
        </div>

        <div className="mt-12">
          <a
            href="#contact"
            data-testid="services-cta"
            className="inline-flex items-center gap-2 font-display font-bold text-[#1B4332] hover:text-[#081C15] transition-colors duration-200"
          >
            Discuss your barn's requirements
            <ArrowRight size={18} />
          </a>
        </div>
      </div>
    </section>
  );
};
